import type { SessionData } from '#auth';
import type { CheckfloProfile } from '../common/dtos';
import { AccountAppType } from '../common/enums/account-app-type';
import { AccountStatus } from '../common/enums/account-status';
import { DwollaCustomerStatus } from '../common/enums/dwolla-customer-status';
import { DwollaCustomerType } from '../common/enums/dwolla-customer-type';
import { MoovCustomerStatus } from '../common/enums/moov-customer-status';
import { StripeVerificationStatus } from '../common/enums/stripe-verification-status';
import { isSignUpRequired } from './extract-requirements';

/**
 * Invited senders must finish Moov before leaving signup (pending / in-review / unverified / disabled).
 * `null` is grandfathered for existing senders who never started Moov.
 * Receive-only accounts are never held for Moov.
 */
export const isSenderMoovSignupIncomplete = (auth: SessionData): boolean => {
  if (!auth.isSender || auth.type === DwollaCustomerType.ReceiveOnly) {
    return false;
  }

  const status = auth.verifiedWithMoov;
  if (status == null) {
    return false;
  }

  return (
    status === MoovCustomerStatus.Pending ||
    status === MoovCustomerStatus.InReview ||
    status === MoovCustomerStatus.Unverified ||
    status === MoovCustomerStatus.Disabled
  );
};

/**
 * Approved AgentSnap senders who finished sign-up stay out of the dashboard until Stripe is Verified.
 * Accounts with sign-up requirements left are routed to that step instead.
 */
export const isAgentSnapStripeVerificationIncomplete = (auth: SessionData): boolean =>
  auth.accountAppType === AccountAppType.AgentSnap &&
  auth.type !== DwollaCustomerType.ReceiveOnly &&
  auth.approved &&
  !isSignUpRequired(auth.requirements) &&
  auth.stripeVerificationStatus?.id !== StripeVerificationStatus.Verified;

export type MoovSignupHoldDestination = 'document' | 'verification-in-progress' | null;

/**
 * Moov document vs wait destination. Used by new-sign-up/index to choose
 * `/new-signup/document` vs `/new-sign-up/verification-in-progress`.
 * `null` means they may leave (enabled, or grandfathered never-started).
 */
export const resolveMoovSignupHoldDestination = (auth: SessionData): MoovSignupHoldDestination => {
  const hasDocumentRequirements = auth.requirements?.some(
    (r: string) => r.startsWith('document.') || r.startsWith('moov.document.'),
  );

  if (hasDocumentRequirements) {
    return 'document';
  }

  if (isSenderMoovSignupIncomplete(auth)) {
    return 'verification-in-progress';
  }

  return null;
};

/**
 * Dwolla or Moov is still verifying the sender, so bank and paper check setup must wait.
 * Banks added before Moov is enabled are not linked to Moov when created (SR-2363).
 */
export const isBankSetupOnHold = (auth: SessionData): boolean => {
  if (resolveMoovSignupHoldDestination(auth) !== null) {
    return true;
  }

  return (
    auth.verifiedWithDwolla === DwollaCustomerStatus.Pending || auth.controllerVerified === DwollaCustomerStatus.Pending
  );
};

/**
 * Combined Dwolla / Moov / Stripe / Checkflo wait flag for middleware and the
 * verification-in-progress page. Moov hold is defined by
 * `resolveMoovSignupHoldDestination`; destination `document` is not "in progress"
 * so the document page can show.
 */
export const isVerificationInProgress = (
  auth: SessionData,
  checkfloProfile: CheckfloProfile,
  bankForPaymentsId: string,
) => {
  const hasDocumentRequirements = auth.requirements?.some(
    (r: string) => r.startsWith('document.') || r.startsWith('moov.document.'),
  );
  const isDwollaVerificationPending: boolean =
    !hasDocumentRequirements &&
    (auth.verifiedWithDwolla === DwollaCustomerStatus.Pending ||
      auth.controllerVerified === DwollaCustomerStatus.Pending);
  const isMoovVerificationPending: boolean = resolveMoovSignupHoldDestination(auth) === 'verification-in-progress';
  const isStipeVerificationPending: boolean =
    auth.accountAppType === AccountAppType.AgentSnap &&
    auth.type !== DwollaCustomerType.ReceiveOnly &&
    (auth.stripeVerificationStatus?.id === StripeVerificationStatus.Pending || auth.isStripePersonVerificationPending);
  const isCheckfloProfileExists = Boolean(
    checkfloProfile?.bankStatus && Object.keys(checkfloProfile.bankStatus).length,
  );
  const isCheckfloProfileActive: boolean = Boolean(
    checkfloProfile.bankStatus?.[bankForPaymentsId] === AccountStatus.Activated,
  );
  const isCheckfloPending: boolean = isCheckfloProfileExists && !isCheckfloProfileActive;

  return (
    isDwollaVerificationPending ||
    isMoovVerificationPending ||
    isStipeVerificationPending ||
    isAgentSnapStripeVerificationIncomplete(auth) ||
    isCheckfloPending
  );
};
